/**
 * Progressive enhancement for the product page.
 *
 * Without JavaScript the gallery shows the first image, the quantity field is a number input and
 * the form posts to /cart/add. This adds thumbnail switching, the quantity steppers and a price
 * that follows the selected variant.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { addWithinMax, MaxQuantityError } from '@theme/cart-add';
import { syncEanInput, withEan, captureExtra } from '@theme/ean-property';

const section = document.querySelector('.product-main');

if (section) {
  const thumbs = section.querySelectorAll('.product-main__thumb');
  const slides = section.querySelectorAll('.product-main__slide');

  const show = (index) => {
    const target = slides[(index + slides.length) % slides.length];
    if (!target) return;

    slides.forEach((slide) => {
      slide.hidden = slide !== target;
    });

    thumbs.forEach((thumb) => {
      const active = thumb.dataset.media === target.dataset.media;
      thumb.classList.toggle('product-main__thumb--active', active);
      thumb.setAttribute('aria-current', String(active));
    });
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      show(Array.from(slides).findIndex((slide) => slide.dataset.media === thumb.dataset.media));
    });
  });

  const qty = section.querySelector('.product-main__qty');
  // custom.max_order_quantity, 0 when the product has no limit.
  const maxQuantity = Number(qty?.dataset.maxQuantity) || 0;
  const productId = Number(qty?.dataset.productId);

  // A typed number above the maximum snaps back to it.
  if (maxQuantity) {
    qty.addEventListener('change', () => {
      if (Number(qty.value) > maxQuantity) qty.value = String(maxQuantity);
    });
  }

  section.querySelectorAll('.product-main__step').forEach((step) => {
    step.addEventListener('click', () => {
      const next = Number(qty.value) + Number(step.dataset.step);
      qty.value = String(Math.min(maxQuantity || Infinity, Math.max(1, next)));
      qty.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  const amount = section.querySelector('.product-main__amount');
  const compare = section.querySelector('.product-main__compare');
  const total = section.querySelector('.product-main__submit-total');
  const priceBlock = section.querySelector('[data-price-block]');
  const giftSummary = section.querySelector('[data-gift-summary]');
  const giftInputs = section.querySelectorAll('[data-gift-price]');
  const giftCardSlot = section.querySelector('[data-gift-card-slot]');
  const currency = section.dataset.currency;
  const moneyFormat = section.dataset.moneyFormat;

  // The submit total starts at the current variant's price and grows with every checked gift
  // extra, so it always tracks what add-to-cart will actually charge.
  let basePriceCents = Number(priceBlock?.dataset.priceCents) || 0;

  /**
   * Recomputes the button total from the selected variant plus every checked gift extra, and
   * keeps the collapsed gift summary in step with what is actually checked.
   *
   * Every gift extra is a real product added as its own cart line (see the submit handler
   * below), so its price belongs in the total add-to-cart will really charge (spec AC 8).
   */
  const updateTotal = () => {
    let extrasCount = 0;
    let extrasCents = 0;

    giftInputs.forEach((input) => {
      if (input.checked) {
        extrasCount += 1;
        extrasCents += Number(input.dataset.giftPrice) || 0;
      }
    });

    // A chosen greeting card is always one card, whatever the product quantity (spec
    // wenskaart-kiezen AC 3).
    if (giftCardSlot instanceof HTMLElement && giftCardSlot.dataset.giftCardVariantId) {
      extrasCount += 1;
      extrasCents += Number(giftCardSlot.dataset.giftCardPriceCents) || 0;
    }

    if (total) total.textContent = formatMoney(basePriceCents + extrasCents, moneyFormat, currency);

    if (giftSummary) {
      giftSummary.hidden = extrasCount === 0;
      if (extrasCount > 0) {
        const label = extrasCount === 1 ? 'extra geselecteerd' : "extra's geselecteerd";
        giftSummary.textContent = `${extrasCount} ${label}`;
      }
    }
  };

  // The hidden properties[EAN] input always follows the checked size. A blank barcode disables the
  // input, so no EAN property is sent at all. It is synced on load, on change, on pageshow (the
  // browser may restore another checked size from the bfcache) and right before submit.
  const eanInput = section.querySelector('[data-ean-input]');
  const syncEan = () => {
    const checkedVariant = section.querySelector('.product-main__variant input:checked');
    if (checkedVariant && eanInput instanceof HTMLInputElement) syncEanInput(eanInput, checkedVariant);
  };
  syncEan();
  window.addEventListener('pageshow', syncEan);
  eanInput?.form?.addEventListener('submit', syncEan, true);

  section.querySelectorAll('.product-main__variant input').forEach((input) => {
    input.addEventListener('change', () => {
      syncEan();
      const discounted = Boolean(input.dataset.compare);

      if (amount) {
        amount.textContent = input.dataset.price;
        amount.classList.toggle('price--sale', discounted);
      }

      if (compare) {
        compare.textContent = input.dataset.compare;
        compare.hidden = !discounted;
      }

      basePriceCents = Number(input.dataset.priceCents) || 0;
      updateTotal();
    });
  });

  giftInputs.forEach((input) => {
    input.addEventListener('change', updateTotal);
  });

  // With a themed wrap product the select (sections/product-main.liquid) is shown while the box is
  // checked, and the chosen option moves the checkbox's variant id, price and value along, so
  // updateTotal and the submit handler keep reading the checkbox only.
  const giftThemeRow = section.querySelector('[data-gift-wrap-theme]');
  const giftThemeSelect = section.querySelector('[data-gift-wrap-theme-select]');
  const giftThemeBox = section.querySelector('[data-gift-wrap-variant-id]');

  const syncGiftTheme = () => {
    if (!(giftThemeSelect instanceof HTMLSelectElement) || !(giftThemeBox instanceof HTMLInputElement)) return;
    const option = giftThemeSelect.selectedOptions[0];
    if (!option) return;
    giftThemeBox.dataset.giftWrapVariantId = option.value;
    giftThemeBox.dataset.giftPrice = option.dataset.price ?? '';
    giftThemeBox.dataset.ean = option.dataset.ean ?? '';
    giftThemeBox.value = option.dataset.title || 'Ja';
    if (giftThemeRow) giftThemeRow.hidden = !giftThemeBox.checked;
    updateTotal();
  };

  giftThemeSelect?.addEventListener('change', syncGiftTheme);
  giftThemeBox?.addEventListener('change', syncGiftTheme);

  // assets/gift-card-picker.js announces every pick and removal of a greeting card.
  giftCardSlot?.addEventListener('gift-card:change', updateTotal);

  // A checkbox can come back checked from the browser's bfcache after a back navigation, so the
  // total and summary need one pass on load to match whatever is actually checked.
  // The same goes for the theme select, which the browser may restore to another option.
  if (giftThemeSelect) syncGiftTheme();
  else if (giftInputs.length > 0) updateTotal();

  const form = section.querySelector('form[action*="/cart/add"]');
  const bubble = document.querySelector('.cart-bubble');
  const bubbleCount = document.querySelector('.cart-bubble__text-count');
  const giftWrapCheckbox = section.querySelector('[data-gift-wrap-variant-id]');

  /**
   * Builds the "Voor: <title>" label for the wrap line from what is currently selected, so it
   * always names the product (and variant, when the product has more than one) the customer is
   * actually adding (spec AC 3).
   *
   * @returns {string}
   */
  const giftForLabel = () => {
    const title = section.querySelector('.product-main__title')?.textContent?.trim() ?? '';
    const variantTitle = section.querySelector('.product-main__variant input:checked')?.dataset.variantTitle;
    return variantTitle ? `${title} - ${variantTitle}` : title;
  };

  if (form) {
    let adding = false;

    const GIFT_WRAP_ERROR = 'Inpakken lukte niet, probeer het opnieuw.';
    const GIFT_CARD_ERROR = 'De wenskaart kon niet worden toegevoegd, probeer het opnieuw.';

    /**
     * The key (_gift_wrap_key or _gift_card_key, both carry the same value) of a gift add whose
     * outcome is still unknown (the request failed without a readable answer and the cart could
     * not be checked yet). The next gift add checks the cart for it first instead of adding again.
     *
     * @type {string | null}
     */
    let unresolvedGiftKey = null;

    /** @returns {Promise<any>} The full cart; rejects on any failed read. */
    const readCart = async () => {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`cart.js ${response.status}`);
      return response.json();
    };

    /**
     * @param {any} cart
     * @param {string} key
     */
    const cartHasGiftKey = (cart, key) =>
      (cart.items ?? []).some(
        (item) => item.properties?._gift_wrap_key === key || item.properties?._gift_card_key === key
      );

    /**
     * Posts to /cart/add.js. A refused add comes back as Shopify's error payload (with
     * `status`); a response without a readable answer rejects.
     *
     * @param {RequestInit} init
     * @returns {Promise<any>}
     */
    const postAdd = async (init) => {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', { method: 'POST', ...init });
      const body = await response.json();
      if (!response.ok && !body?.status) throw new Error(`cart/add.js ${response.status}`);
      return body;
    };

    /**
     * Listens for the drawer's own open event, so the spinner hands over exactly when the drawer
     * takes the stage. Call it before announcing the add: the drawer may open in the same tick.
     * Falls through when the drawer does not auto-open or is already open, and gives up after a
     * second so a missing drawer can never keep the button spinning.
     *
     * @returns {Promise<void>}
     */
    const drawerOpens = () => {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      const autoOpens = document.querySelector('cart-drawer-component')?.hasAttribute('auto-open');

      if (!drawer || !autoOpens || drawer.hasAttribute('open')) return Promise.resolve();

      return new Promise((resolve) => {
        const done = () => {
          clearTimeout(timeout);
          document.removeEventListener('theme-drawer:open', done);
          slidIn().then(resolve);
        };
        const timeout = setTimeout(done, 1000);

        document.addEventListener('theme-drawer:open', done, { once: true });
      });
    };

    /**
     * Resolves when the drawer has finished sliding in, so the button label only returns once the
     * drawer stands still. Gives up after half a second, longer than the slide itself.
     *
     * @returns {Promise<void>}
     */
    const slidIn = () => {
      const panel = document.querySelector('theme-drawer#cart-drawer dialog');

      if (!panel) return Promise.resolve();

      return new Promise((resolve) => {
        const done = () => {
          clearTimeout(timeout);
          panel.removeEventListener('animationend', done);
          resolve();
        };
        const timeout = setTimeout(done, 500);

        panel.addEventListener('animationend', done, { once: true });
      });
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      // A second click while the first add is in flight would add the line twice.
      if (adding) return;
      adding = true;

      const submit = form.querySelector('.product-main__submit');
      submit?.setAttribute('aria-busy', 'true');
      submit?.setAttribute('disabled', '');

      const formData = new FormData(form);

      // The cart drawer re-renders itself from the sections the Cart AJAX API returns, so ask
      // for every section that holds a cart-items-component.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((item) => item.dataset.sectionId)
        .filter(Boolean);

      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const quantity = Number(formData.get('quantity')) || 1;
      const error = form.querySelector('[data-cart-error]');

      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      const mainVariantId = String(formData.get('id') ?? '');
      // Each extra's EAN is captured together with its variant id, before any await, so a theme
      // or card change during the retry's cart read cannot pair a line with another EAN.
      const giftWrap = giftWrapCheckbox instanceof HTMLInputElement && giftWrapCheckbox.checked
        ? captureExtra(giftWrapCheckbox.dataset.giftWrapVariantId, giftWrapCheckbox)
        : null;
      const giftWrapVariantId = giftWrap?.variantId || null;

      // The card picker (assets/gift-card-picker.js) keeps the chosen card on the slot.
      const giftCard = giftCardSlot instanceof HTMLElement && giftCardSlot.dataset.giftCardVariantId
        ? {
            ...captureExtra(giftCardSlot.dataset.giftCardVariantId, giftCardSlot),
            title: giftCardSlot.dataset.giftCardTitle ?? '',
            text: (section.querySelector('[data-gift-card-text]')?.value ?? '').trim(),
          }
        : null;
      const hasGiftExtras = Boolean(giftWrapVariantId || giftCard);

      const showError = (message) => {
        if (!error) return;
        error.textContent = message;
        error.hidden = false;
      };

      const lines = [{ merchandiseId: mainVariantId, quantity }];
      if (giftWrapVariantId) lines.push({ merchandiseId: giftWrapVariantId, quantity });
      if (giftCard) lines.push({ merchandiseId: giftCard.variantId, quantity: 1 });

      /**
       * Updates the bubble and announces the added lines, with the rendered sections when the
       * add response carried them (the drawer re-renders itself otherwise).
       *
       * @param {any} cart - A full /cart.js response.
       * @param {Record<string, string> | undefined} sections
       */
      const announceAdded = async (cart, sections) => {
        if (bubble && bubbleCount) {
          bubbleCount.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
          bubbleCount.classList.toggle('hidden', cart.item_count === 0);
          bubble.classList.toggle('visually-hidden', cart.item_count === 0);
        }

        // Announced only once the line is really in the cart, and with the rendered sections
        // already in hand: the drawer then opens straight onto its new contents.
        const deferred = CartLinesUpdateEvent.createPromise();
        const opened = drawerOpens();

        form.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines,
            promise: deferred.promise,
          })
        );

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-main',
            itemCount: quantity,
            sections,
            didError: false,
          },
        });

        await opened;
      };

      /**
       * After a partial gift add, puts the wrap line back at the product's quantity (a wrap per
       * unit, like a complete add) and announces the resulting cart. Best effort: when the cart
       * cannot be read, the error message already shown stays the customer's cue.
       *
       * @param {string} key
       */
      const reconcilePartialGiftAdd = async (key) => {
        try {
          let current = await readCart();
          const items = current.items ?? [];
          const main = items.find(
            (item) =>
              !item.parent_relationship &&
              (item.properties?._gift_wrap_key === key || item.properties?._gift_card_key === key)
          );
          if (!main) return;

          const wrap = items.find(
            (item) => item.parent_relationship && item.properties?._gift_wrap_key === key
          );

          if (wrap && wrap.quantity !== main.quantity) {
            const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: wrap.key, quantity: main.quantity }),
            });
            if (response.ok) current = await response.json();
          }

          await announceAdded(current, undefined);
        } catch {
          // Leave it at the message: the cart page shows the real state on the next visit.
        }
      };

      // The cart page names what could not be confirmed: the card whenever one was chosen.
      const giftErrorCartPath = giftCard ? 'cart?gift_card_error=1' : 'cart?gift_wrap_error=1';

      // Set once /cart/add.js has definitely added the lines, so no failure after that point can
      // lead to adding them a second time.
      let addSucceeded = false;

      // The key sent with a gift add, kept for reconciling a partial add below.
      let sentGiftKey = null;

      // custom.max_order_quantity counts every line of this product; addWithinMax runs the post
      // only after the cart check, queued behind any other limited add on the page.
      const limit = { productId, maxQuantity, quantity };

      try {
        let added = null;
        let cart = null;

        if (hasGiftExtras) {
          // The previous gift add ended without a readable answer and the cart could not be
          // checked then: look now, before adding again, so the product never lands twice.
          if (unresolvedGiftKey) {
            const current = await readCart();
            const previousKey = unresolvedGiftKey;
            unresolvedGiftKey = null;
            if (cartHasGiftKey(current, previousKey)) cart = current;
          }

          if (!cart) {
            // One items[] request for every line: if the wrap or the card cannot be added,
            // /cart/add.js adds none of them, so the product never lands in the cart without
            // what was chosen with it and nothing unpaid lands either (spec randgeval
            // "toevoegen mislukt"). parent_id nests the wrap and card lines under the main line,
            // so Shopify removes them together with their product (card spec AC 4).
            const giftKey = `gw_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const mainProperties = {};

            for (const [key, value] of formData.entries()) {
              const match = key.match(/^properties\[(.+)\]$/);
              if (match && typeof value === 'string' && value !== '') {
                mainProperties[match[1]] = value;
              }
            }

            const items = [{ id: mainVariantId, quantity, properties: mainProperties }];

            if (giftWrapVariantId) {
              mainProperties._gift_wrap_key = giftKey;
              items.push({
                id: giftWrapVariantId,
                quantity,
                parent_id: Number(mainVariantId),
                properties: withEan(
                  { Voor: giftForLabel(), _gift_wrap_key: giftKey },
                  giftWrap.ean
                ),
              });
            }

            if (giftCard) {
              // The product line names its card; the card line itself says which product it
              // belongs to and what to write on it, one card whatever the quantity (card spec
              // AC 3). An empty text sends no Kaarttekst at all rather than a blank one.
              mainProperties.Wenskaart = giftCard.title;
              mainProperties._gift_card_key = giftKey;
              const cardProperties = { Voor: giftForLabel() };
              if (giftCard.text) cardProperties.Kaarttekst = giftCard.text;
              cardProperties._gift_card_key = giftKey;
              items.push({
                id: giftCard.variantId,
                quantity: 1,
                parent_id: Number(mainVariantId),
                properties: withEan(cardProperties, giftCard.ean),
              });
            }

            added = await addWithinMax(limit, () => {
              // Until a readable answer arrives, the outcome of this request is unknown.
              unresolvedGiftKey = giftKey;
              sentGiftKey = giftKey;
              return postAdd({
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  items,
                  sections: sectionIds.join(','),
                  sections_url: window.location.pathname,
                }),
              });
            });
            unresolvedGiftKey = null;
          }
        } else {
          added = await addWithinMax(limit, () =>
            postAdd({
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: formData,
            })
          );
        }

        // The cart refused the lines (sold out, or the whole stock is already in the cart).
        // Say why. A plain refusal changed nothing, so the drawer stays as it is.
        if (added?.status) {
          showError(added.description || added.message || 'Dit product kan niet worden toegevoegd.');

          // A 422 on stock can still add what was available ("slechts 36 toegevoegd"), gift lines
          // included. Settle the cart once: the wrap follows the product's real quantity, and the
          // bubble and drawer show what did land instead of pretending nothing happened.
          if (sentGiftKey) await reconcilePartialGiftAdd(sentGiftKey);
          return;
        }

        addSucceeded = true;

        await announceAdded(cart ?? (await readCart()), added?.sections);
      } catch (reason) {
        if (reason instanceof MaxQuantityError) {
          // Over the maximum, or the cart could not be checked: nothing was posted.
          showError(reason.message);
        } else if (addSucceeded) {
          // The lines are in the cart, only refreshing the drawer failed: show the cart page
          // rather than posting the form, which would add everything a second time.
          window.location.assign(window.Shopify.routes.root + 'cart');
        } else if (!hasGiftExtras && !maxQuantity) {
          // Plain add: the native form is a safe fallback, it posts exactly what was chosen.
          form.submit();
        } else if (!hasGiftExtras) {
          // Limited product, outcome unknown: a native post could land on top of an add that did
          // go through and pass the maximum, so show the real cart instead.
          window.location.assign(window.Shopify.routes.root + 'cart');
        } else if (unresolvedGiftKey) {
          // The wrapped add may or may not have gone through (network error or an unreadable
          // response, not a definite Shopify refusal, which already returned above without
          // throwing). Never post the native form and never invite a retry with a new key here:
          // either would risk adding the pair twice. Read the real cart once and settle for what
          // it shows instead.
          try {
            const current = await readCart();
            const found = cartHasGiftKey(current, unresolvedGiftKey);
            unresolvedGiftKey = null;

            if (found) {
              await announceAdded(current, undefined);
            } else {
              // Genuinely never landed: send the customer to the real cart state rather than a
              // "probeer het opnieuw" prompt that could add it a second time. The query flag
              // lets the cart page show why, without a second POST.
              window.location.assign(window.Shopify.routes.root + giftErrorCartPath);
            }
          } catch {
            // Still unreadable: the outcome stays unknown, so no second POST either. Show the
            // customer the actual cart instead of asking them to retry, with the same flag.
            window.location.assign(window.Shopify.routes.root + giftErrorCartPath);
          }
        } else {
          showError(giftCard ? GIFT_CARD_ERROR : GIFT_WRAP_ERROR);
        }
      } finally {
        adding = false;
        submit?.removeAttribute('aria-busy');
        submit?.removeAttribute('disabled');
      }
    });

    // The checkbox ships disabled (sections/product-main.liquid) so nothing unpaid can be
    // submitted while no handler is listening; now that one is, wrapping is a safe choice.
    giftWrapCheckbox?.removeAttribute('disabled');
    // Same for the greeting card: its line only exists through the handler above.
    section.querySelectorAll('[data-gift-card-open]').forEach((opener) => opener.removeAttribute('disabled'));
  }

  // Een uitverkochte maat is geen doodlopend chipje: hij opent de terug-op-voorraadmelding voor
  // precies die maat, met de maat in de link mee zodat het formulier weet waar het over gaat.
  const notify = section.querySelector('[data-notify-panel]');

  if (notify) {
    const notifySize = notify.querySelector('[data-notify-size]');
    const notifyLink = notify.querySelector('[data-notify-link]');
    const notifyBase = notifyLink?.dataset.notifyBase || '/pages/contact';
    const soldOut = section.querySelectorAll('[data-notify-variant]');

    soldOut.forEach((chip) => {
      chip.addEventListener('click', () => {
        const open = chip.getAttribute('aria-expanded') !== 'true';

        soldOut.forEach((other) => {
          other.setAttribute('aria-expanded', String(other === chip && open));
        });

        notify.hidden = !open;

        if (!open) return;

        if (notifySize) notifySize.textContent = chip.dataset.notifyTitle;

        if (notifyLink) {
          const url = new URL(notifyBase, window.location.origin);
          url.searchParams.set('product', notify.dataset.notifyProduct);
          url.searchParams.set('handle', notify.dataset.notifyHandle);
          url.searchParams.set('variant', chip.dataset.notifyVariant);
          url.searchParams.set('maat', chip.dataset.notifyTitle);
          notifyLink.href = url.toString();
        }
      });
    });
  }
}
